import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Avatar,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  DateRange,
  Event,
  FilterAltOutlined,
  Person,
  SearchOutlined,
  Edit,
  Delete,
  CloudUpload,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CustomTextField from '../../../common/Form/TextField';
import {
  useGetAllEventsQuery,
  usePostEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
} from '../eventsApiSlice';
import toast, { Toaster } from 'react-hot-toast';
import LocationIcon from '@mui/icons-material/LocationOn';
import LinkIcon from '@mui/icons-material/Link';
import './EventList.css'; // Import CSS for dialog background blur effect
import VisibilityIcon from '@mui/icons-material/Visibility';
import eventsbg from '../../../assets/eventlistbg5.png';
import { Tooltip } from '@mui/material';
import UserAvatar from '../../../common/UserAvatar';
import { variants } from '../../../variants/variants';
import AttachAvatar from '../../../common/AttachAvatar';
import { selectCurrentUserEmpCode, setEventsStatus } from '../../Auth/authSlice';
import { useDispatch, useSelector } from 'react-redux';

const theme = createTheme({
  typography: {
    h4: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#222',
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
      color: '#555',
    },
  },
  palette: {
    primary: {
      main: '#1976d2',
    },
    text: {
      primary: '#000',
      secondary: '#555',
    },
  },
});

const EventsList = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetAllEventsQuery();
  const events = data?.data || [];
  const dispatch = useDispatch();
  const [postEvent] = usePostEventMutation();
  const [updateEvent] = useUpdateEventMutation();
  const [deleteEvent] = useDeleteEventMutation();
  const empCode = useSelector(selectCurrentUserEmpCode);
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [newEvent, setNewEvent] = useState({
    eventName: '',
    subtitle: '',
    poster: null,
    description: '',
    contactPerson: {
      name: '',
      email: '',
      skype: '',
    },
    eventDetails: {
      startDate: '',
      endDate: '',
      time: '',
      venue: '',
      lastDateOfRegistration: '',
      registrationLink: '',
    },
  });

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleCardClick = (event, id) => {
    navigate(`${id}`);
  };

  const handleAddEventClick = () => {
    setEditingEventId(null);
    setOpen(true);
  };

  const handleEditClick = (event) => {
    if (event && event._id) {
      setEditingEventId(event._id);
      setNewEvent(event);
      setOpen(true);
    } else {
      console.error('Invalid event object:', event);
    }
  };

  const handleDeleteClick = async (id) => {
    try {
      await deleteEvent(id).unwrap();
      toast.success('Event deleted successfully!');
    } catch (err) {
      toast.error('Failed to delete the event: ' + err.message);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const appendFormData = (formData, data, parentKey = '') => {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
      Object.keys(data).forEach((key) => {
        appendFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
      });
    } else {
      formData.append(parentKey, data);
    }
  };

  const handleSave = async () => {
    const formData = new FormData();
    appendFormData(formData, newEvent);
    formData.append('empCode', empCode);
    try {
      if (editingEventId) {
        await updateEvent({ id: editingEventId, formData }).unwrap();
        toast.success('Event updated successfully!');
      } else {
        await postEvent(formData).unwrap();
        toast.success('Event added successfully!');
      }
      setOpen(false);
      setNewEvent({
        eventName: '',
        subtitle: '',
        poster: null,
        description: '',
        contactPerson: {
          name: '',
          email: '',
          skype: '',
        },
        eventDetails: {
          startDate: '',
          endDate: '',
          time: '',
          venue: '',
          lastDateOfRegistration: '',
          registrationLink: '',
        },
      });
    } catch (err) {
      console.error('Failed to save the event:', err);
      toast.error('Failed to save the event: ' + err.message);
    }
  };

  useEffect(() => {
    dispatch(setEventsStatus(false));
  }, [events]);

  const [activeField, setActiveField] = useState('');

  const handleTextFieldClick = (fieldName) => {
    setActiveField(fieldName);
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
    setNewEvent({ ...newEvent, poster: e.target.files[0] });
  };

  // Filter events based on the search query
  const filteredEvents = events.filter(
    (event) =>
      event.eventName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <ThemeProvider theme={theme}>
      <Grid
        // maxWidth="md"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'absolute',
              top: 15,
              width: '59.5vw',
            }}
          >
            <CustomTextField
              value={searchQuery}
              onChange={handleSearchChange}
              className='search'
              size='small'
              placeholder='Search'
              sx={{
                backgroundColor: 'white',
                fontSize: theme.typography.body1.fontSize,
                borderBottom: 1,
                borderColor: 'divider',
                borderRadius: '10px',
                width: '85%',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchOutlined />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton>
                      <FilterAltOutlined />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              sx={{ backgroundColor: '#0a4fac', padding: '7px' }}
              size='small'
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleAddEventClick}
            >
              Add Event
            </Button>
          </Box>
          <Grid
            maxWidth='xl'
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box sx={{ marginTop: '4.5rem', overflow: 'auto', height: '84vh' }}>
              {filteredEvents.map((event, index) => (
                <Card
                  onClick={() => handleCardClick(event, event._id)}
                  key={index}
                  sx={{
                    borderRadius: '8px',
                    cursor: 'default',
                    height: '6rem',
                    width: '60vw',
                    mb: 3,
                    borderLeft: '5px solid #0a4fac',
                    backgroundColor: 'transparent',
                    backgroundImage: `url(${eventsbg})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    overflow: 'hidden',
                    // transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      // transform: 'scale(1.005)',
                      boxShadow: variants.BOX_SHADOW,
                    },
                  }}
                >
                  <CardContent>
                    <Grid container spacing={4}>
                      <Grid
                        item
                        xs={2}
                        sx={{
                          display: 'flex',
                          marginLeft: '-1rem',
                          marginTop: '-1rem',

                          // alignItems: 'center',
                          // justifyContent: 'center',
                        }}
                      >
                        {event.poster && (
                          <AttachAvatar
                            // variant='rounded'
                            src={event.poster[0]}
                            alt={event.eventName}
                            variant='rounded'
                            sx={{ height: 100, width: 150, objectFit: 'cover' }}
                          />
                        )}
                      </Grid>

                      <Grid item xs={5}>
                        <Typography variant='subtitle1'>
                          {event.eventName} - {event.subtitle}
                        </Typography>
                        <Typography variant='subtitle2' color='textSecondary'></Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          {/* <Person fontSize='small' sx={{ color: '#555', mr: 0.5 }} /> */}
                          <Typography variant='body2' color='textSecondary'>
                            {/* {event.contactPerson.name} */}
                            Posted by devmankad on 17/06/2024
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={2}>
                        <Box></Box>
                      </Grid>
                      <Box
                        display='flex'
                        justifyContent='flex-end'
                        flexDirection='row'
                        height='100%'
                        sx={{ marginTop: '2.5rem', marginLeft: '7rem' }}
                      >
                        {events?.empCode === empCode ? (
                          <>
                            <Tooltip title='Edit'>
                              <IconButton onClick={() => handleEditClick(event)}>
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title='Delete'>
                              <IconButton onClick={() => handleDeleteClick(event._id)}>
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : null}
                      </Box>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Grid>
        </Box>
      </Grid>
      <Dialog
        open={open}
        maxWidth='md'
        onClose={handleClose}
        PaperProps={{
          style: {
            backgroundColor: 'rgba(255, 255, 255)', // Semi-transparent background color
            backdropFilter: 'blur(10px)', // Background blur effect
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', // Box shadow for depth
            borderRadius: '12px', // Rounded corners
            margin: 'auto', // Center the dialog horizontally
            padding: '20px', // Add some padding
          },
        }}
      >
        <DialogTitle>Add New Event</DialogTitle>

        <DialogContent className='dialog-content'>
          <Grid item xs={12} sx={{ display: 'flex', float: 'right' }}>
            {selectedFile && (
              <Typography variant='caption' sx={{ margin: '8px', color: 'grey' }}>
                Selected File: {selectedFile.name}
              </Typography>
            )}
            <Button
              size='small'
              variant='outlined'
              component='label'
              startIcon={<CloudUpload />}
              sx={{ borderRadius: '8px' }}
            >
              Upload Poster
              <input type='file' onChange={handleFileChange} style={{ display: 'none' }} />
            </Button>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant='subtitle1' gutterBottom style={{ display: 'flex', alignItems: 'center' }}>
                <Event style={{ marginRight: '8px' }} /> Event Details
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Event Name'
                value={newEvent.eventName}
                onChange={(e) => setNewEvent({ ...newEvent, eventName: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Subtitle'
                value={newEvent.subtitle}
                onChange={(e) => setNewEvent({ ...newEvent, subtitle: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Description'
                multiline
                rows={4}
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant='subtitle1' gutterBottom style={{ display: 'flex', alignItems: 'center' }}>
                <Person style={{ marginRight: '8px' }} /> Contact Person
              </Typography>
            </Grid>
            <Grid item xs={activeField === 'name' ? 6 : 3}>
              <TextField
                fullWidth
                label='Name'
                value={newEvent.contactPerson.name}
                onClick={() => handleTextFieldClick('name')}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    contactPerson: { ...newEvent.contactPerson, name: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={activeField === 'email' ? 6 : 3}>
              <TextField
                fullWidth
                label='Email'
                type='email'
                value={newEvent.contactPerson.email}
                onClick={() => handleTextFieldClick('email')}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    contactPerson: { ...newEvent.contactPerson, email: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={activeField === 'skype' ? 6 : 3}>
              <TextField
                fullWidth
                label='Skype'
                value={newEvent.contactPerson.skype}
                onClick={() => handleTextFieldClick('skype')}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    contactPerson: { ...newEvent.contactPerson, skype: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant='subtitle1' gutterBottom style={{ display: 'flex', alignItems: 'center' }}>
                <DateRange style={{ marginRight: '8px' }} /> Venue & Dates
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Start Date'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={newEvent.eventDetails.startDate}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    eventDetails: { ...newEvent.eventDetails, startDate: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='End Date'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={newEvent.eventDetails.endDate}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    eventDetails: { ...newEvent.eventDetails, endDate: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Time'
                type='time'
                InputLabelProps={{ shrink: true }}
                value={newEvent.eventDetails.time}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    eventDetails: { ...newEvent.eventDetails, time: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Venue'
                value={newEvent.eventDetails.venue}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    eventDetails: { ...newEvent.eventDetails, venue: e.target.value },
                  })
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <LocationIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                InputLabelProps={{ shrink: true }}
                label='Last Date Of Registration'
                type='date'
                value={newEvent.eventDetails.lastDateOfRegistration}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    eventDetails: { ...newEvent.eventDetails, lastDateOfRegistration: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Registration Link'
                type='url'
                value={newEvent.eventDetails.registrationLink}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <LinkIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    eventDetails: { ...newEvent.eventDetails, registrationLink: e.target.value },
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color='primary'>
            Cancel
          </Button>
          <Button onClick={handleSave} color='primary'>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Toaster position='top-right' reverseOrder={false} />
    </ThemeProvider>
  );
};

export default EventsList;
